import { ICsvLeadRow } from '../../../services/apis/campaigns/useCampaignsRequest';
import { TSalesNavLeadPreview } from './salesNavListQuery';

/** Sales Nav member id (ACw…) — not classic LinkedIn ACo member id. */
export function isSalesNavProfileId(value?: string | null): boolean {
  const s = String(value || '').trim();
  return /^ACw[A-Za-z0-9_-]{20,}$/i.test(s);
}

/** Classic LinkedIn member id (ACo…). */
export function isClassicMemberProfileId(value?: string | null): boolean {
  const s = String(value || '')
    .replace(/^urn:li:fsd_profile:/, '')
    .replace(/^urn:li:member:/, '')
    .trim();
  return /^ACo[A-Za-z0-9_-]{20,}$/i.test(s);
}

/**
 * Pull SN profile id from searchLeadsSN row.
 * Parser currently puts the SN id in `publicIdentifier` and/or `salesMemberId`.
 */
export function snProfileIdFromLead(p: TSalesNavLeadPreview & { salesMemberId?: string }): string {
  const candidates = [
    (p as any).salesMemberId,
    p.publicIdentifier,
    p.salesEntityUrn?.match(/fs_salesProfile:\(([^,)\s]+)/i)?.[1],
  ];
  for (const c of candidates) {
    const s = String(c || '').trim();
    if (isSalesNavProfileId(s)) return s;
  }
  return '';
}

/** Build sales/lead URL that still carries authType + authToken for later profile fetch. */
export function salesLeadUrlFromEntityUrn(entityUrn?: string | null, snProfileId?: string): string {
  const urn = String(entityUrn || '').trim();
  const m = urn.match(/urn:li:fs_salesProfile:\(([^,)\s]+),([^,)\s]+),([^)\s]+)\)/i);
  if (m) {
    return `https://www.linkedin.com/sales/lead/${m[1]},${m[2]},${m[3]}`;
  }
  const id = String(snProfileId || '').trim();
  return id ? `https://www.linkedin.com/sales/lead/${id}` : '';
}

export function parseSalesLeadAuth(linkedinUrl?: string | null): {
  snProfileId: string;
  authType: string;
  authToken: string;
} | null {
  const raw = String(linkedinUrl || '').trim();
  if (!raw) return null;

  const fromPath = raw.match(
    /sales\/lead\/([^/?#,]+),([^/?#,]+),([^/?#]+)/i,
  );
  if (fromPath) {
    return {
      snProfileId: decodeURIComponent(fromPath[1]),
      authType: decodeURIComponent(fromPath[2]),
      authToken: decodeURIComponent(fromPath[3]),
    };
  }

  const fromUrn = raw.match(
    /fs_salesProfile:\(([^,)\s]+),([^,)\s]+),([^)\s]+)\)/i,
  );
  if (fromUrn) {
    return {
      snProfileId: fromUrn[1],
      authType: fromUrn[2],
      authToken: fromUrn[3],
    };
  }

  return null;
}

/** True vanity slug only — rejects ACo/ACw internal ids. */
export function isRealVanityPublicIdentifier(value?: string | null): boolean {
  const s = String(value || '').trim();
  if (!s) return false;
  if (isSalesNavProfileId(s) || isClassicMemberProfileId(s)) return false;
  if (/^urn:li:/i.test(s)) return false;
  return true;
}

/**
 * Classic LinkedIn profile URL for UI links.
 * Never returns a Sales Navigator sales/lead URL.
 */
export function classicLinkedInProfileUrl(lead: {
  publicIdentifier?: string | null;
  linkedinUrl?: string | null;
}): string {
  if (isRealVanityPublicIdentifier(lead.publicIdentifier)) {
    return `https://www.linkedin.com/in/${String(lead.publicIdentifier).trim()}`;
  }

  const url = String(lead.linkedinUrl || '').trim();
  if (!url || /linkedin\.com\/sales\//i.test(url)) return '';

  const fromIn = url.match(/linkedin\.com\/in\/([^/?#]+)/i);
  if (!fromIn) return '';
  try {
    const slug = decodeURIComponent(fromIn[1]);
    return isRealVanityPublicIdentifier(slug) ? `https://www.linkedin.com/in/${slug}` : '';
  } catch {
    return '';
  }
}

/** Map SN search rows → from-csv enroll payload (identity = snProfileId). */
export function snLeadsToLeadRows(
  profiles: Array<TSalesNavLeadPreview & { salesMemberId?: string }>,
): ICsvLeadRow[] {
  return partitionSnLeadsForImport(profiles).rows;
}

export function snDegreeNumber(degree: unknown): number | null {
  if (degree == null || degree === '') return null;
  const n = typeof degree === 'number' ? degree : Number(degree);
  return Number.isFinite(n) ? n : null;
}

export type TSnImportPartition = {
  rows: ICsvLeadRow[];
  /** Profiles returned from SN fetch (before filtering). */
  loaded: number;
  /** Rows sent to enroll (with SN id, excluding self). */
  toImport: number;
  /** degree === 0 (viewer themselves). */
  skippedSelf: number;
  /** No usable ACw id. */
  skippedNoId: number;
  /** Count among toImport with degree 1. */
  firstDegree: number;
  /** Count among toImport with degree 2 or 3. */
  outreachDegrees: number;
  /** Count among toImport with unknown degree. */
  unknownDegree: number;
};

/**
 * Split SN list leads for campaign enroll:
 * keep 1st/2nd/3rd/unknown, skip only self (degree 0).
 * Prefer ACw; fall back to classic ACo / vanity (Unipile SN search often returns both).
 */
export function partitionSnLeadsForImport(
  profiles: Array<TSalesNavLeadPreview & { salesMemberId?: string }>,
): TSnImportPartition {
  const rows: ICsvLeadRow[] = [];
  const seen = new Set<string>();
  let skippedSelf = 0;
  let skippedNoId = 0;
  let firstDegree = 0;
  let outreachDegrees = 0;
  let unknownDegree = 0;

  for (const p of profiles) {
    const snProfileId = snProfileIdFromLead(p);
    const classicId = isClassicMemberProfileId(p.publicIdentifier)
      ? String(p.publicIdentifier).trim()
      : (isClassicMemberProfileId((p as any).salesMemberId) ? String((p as any).salesMemberId).trim() : '');
    const vanity = isRealVanityPublicIdentifier(p.publicIdentifier)
      ? String(p.publicIdentifier).trim()
      : '';
    const identity = snProfileId || classicId || vanity;
    if (!identity) {
      skippedNoId += 1;
      continue;
    }
    if (seen.has(identity)) continue;
    seen.add(identity);

    const degree = snDegreeNumber(p.degree);
    if (degree === 0) {
      skippedSelf += 1;
      continue;
    }

    if (degree === 1) firstDegree += 1;
    else if (degree === 2 || degree === 3) outreachDegrees += 1;
    else unknownDegree += 1;

    const name = (p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ')).trim();
    rows.push({
      // Prefer real vanity when Unipile provides it; otherwise leave empty for ACw-only rows.
      publicIdentifier: vanity || undefined,
      linkedinId: snProfileId || classicId || vanity,
      firstName: p.firstName || undefined,
      lastName: p.lastName || undefined,
      name: name || undefined,
      title: p.title || undefined,
      company: p.company || undefined,
      location: p.geoRegion || undefined,
      imageUrl: p.profilePicture || undefined,
      linkedinUrl: salesLeadUrlFromEntityUrn(p.salesEntityUrn, snProfileId)
        || (vanity ? `https://www.linkedin.com/in/${vanity}` : undefined),
    });
  }

  return {
    rows,
    loaded: profiles.length,
    toImport: rows.length,
    skippedSelf,
    skippedNoId,
    firstDegree,
    outreachDegrees,
    unknownDegree,
  };
}
